import qs from 'qs';
import { sanitizeClientPerson } from './utils';
import sureAxios from '../services/sureAxios';
import { destructureAxiosReq } from '../services/utils';

/**
 * Create a new client.
 *
 * @param {Object} opts The options.
 * @param {String} opts.client_type The client type. One of: individual, business.
 * @param {Object} opts.broker The broker.
 * @param {String} opts.broker.id The broker id.
 * @param {String} opts.broker.name The broker name.
 * @param {Object} opts.person The client person details.
 * @param {String} opts.person.email The client email.
 * @param {String} opts.person.first_name The client firstName.
 * @param {String} opts.person.last_name The client lastName.
 * @param {String} opts.person.phone_number The client phone number.
 * @param {Object} [opts.person.address] The client address.
 * @param {String} opts.person.address.line1 The client's line1 address.
 * @param {String} [opts.person.address.line2] The client's line2 address.
 * @param {String} opts.person.address.city The client's city.
 * @param {Object} opts.person.address.region The client's region.
 * @param {Object} opts.person.address.region.code The client's region code.
 * @param {String} opts.person.address.postal The client's postal code.
 */
export function createClient(opts = {}) {
  const payload = {
    client_type: opts.client_type,
    broker: opts.broker,
    person: sanitizeClientPerson(opts.person),
  };
  return destructureAxiosReq(
    sureAxios.post(`/api/management/v1/clients`, payload)
  );
}

/**
 * Update a  client.
 *
 * @param {Object} opts The options.
 * @param {String} opts.id The client ID to update.
 * @param {String} opts.client_type The client type. One of: individual, business.
 * @param {Object} opts.broker The broker.
 * @param {String} opts.broker.id The broker id.
 * @param {String} opts.broker.name The broker name.
 * @param {Object} opts.person The client person details.
 * @param {Object} opts.person.dob The client dob.
 * @param {String} opts.person.email The client email.
 * @param {String} opts.person.first_name The client firstName.
 * @param {String} opts.person.last_name The client lastName.
 * @param {String} opts.person.phone_number The client phone number.
 * @param {Object} [opts.person.address] The client address.
 * @param {String} opts.person.address.line1 The client's line1 address.
 * @param {String} [opts.person.address.line2] The client's line2 address.
 * @param {String} opts.person.address.city The client's city.
 * @param {Object} opts.person.address.region The client's region.
 * @param {Object} opts.person.address.region.code The client's region code.
 * @param {Object} opts.person.address.region.name The client's region name.
 * @param {String} opts.person.address.postal The client's postal code.
 */
export function updateClient(opts = {}) {
  const payload = {
    client_type: opts.client_type,
    broker: opts.broker,
    person: sanitizeClientPerson(opts.person),
  };

  return destructureAxiosReq(
    sureAxios.put(`/api/management/v1/clients/${opts.id}`, payload)
  );
}

/**
 * Get single client.
 *
 * @param {Object} opts The options.
 * @param {String} opts.clientId The client ID to fetch.
 */
export function getClient({ clientId }) {
  return destructureAxiosReq(
    sureAxios.get(`/api/management/v1/clients/${clientId}`)
  );
}

/**
 * Get clients list.
 *
 * @param {Object} opts The options.
 * @param {Object} [opts.query] The query filters.
 */
export function getClients({ query = {} }) {
  return destructureAxiosReq(
    sureAxios.get(`/api/management/v1/clients?${qs.stringify(query)}`)
  );
}

/**
 * Get clients stats.
 *
 * @param {Object} opts The options.
 * @param {Object} [opts.filter] The filters.
 */
export function getClientsStats({ filter = {} }) {
  return destructureAxiosReq(
    sureAxios.get(`/api/management/v1/clients/stats?${qs.stringify(filter)}`)
  );
}
